import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Minus, Plus, ShoppingBag, Ticket, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { euro, useCart } from "@/cart";
import { resolveSrc } from "@/components/Gallery";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CartDrawer() {
  const { items, count, subtotal, setQty, open, setOpen, promo, setPromo } = useCart();
  const [shipping, setShipping] = useState(0);
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");

  useEffect(() => {
    fetch(`${API}/api/shop/config`).then((r) => (r.ok ? r.json() : null)).then((c) => setShipping(c?.shipping_cents ?? 0)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    window.__lenis?.stop();
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [open, setOpen]);

  const checkout = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: items.map(({ product_id, size, quantity }) => ({ product_id, size, quantity })), origin_url: window.location.origin, promo_code: promo }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 400 && promo && String(data.detail || "").includes(promo)) setPromo("");
        throw new Error(data.detail || "Errore");
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast(err.message || "Impossibile avviare il pagamento");
      setLoading(false);
    }
  };

  return (
    <>
      {count > 0 && !open && (
        <button
          data-testid="cart-open-button"
          onClick={() => setOpen(true)}
          aria-label={`Carrello, ${count} articoli`}
          className="fixed bottom-4 right-4 z-40 inline-flex h-14 items-center gap-3 border border-red-900/60 bg-[#0B0B0B]/95 px-5 font-mono-tac text-xs uppercase tracking-[0.2em] text-neutral-100 shadow-[0_10px_40px_rgba(0,0,0,0.7)] backdrop-blur transition-colors duration-200 hover:border-[#C8102E] sm:bottom-6 sm:right-6"
        >
          <ShoppingBag className="h-4 w-4 text-red-500" />
          Carrello
          <span data-testid="cart-count" className="flex h-6 min-w-[1.5rem] items-center justify-center bg-[#C8102E] px-1.5 text-[11px] text-white">{count}</span>
        </button>
      )}
      <AnimatePresence>
        {open && (
          <motion.div data-testid="cart-drawer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[90] flex justify-end bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}>
            <motion.aside
              initial={{ x: 80, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 80, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="flex h-full w-full max-w-md flex-col border-l border-red-900/40 bg-[#0B0B0B]"
            >
              <header className="flex items-center justify-between border-b border-neutral-800 p-5">
                <div>
                  <p className="font-mono-tac text-[10px] uppercase tracking-[0.3em] text-red-600">Merch</p>
                  <h2 className="font-display text-3xl uppercase text-neutral-100">Il tuo carrello</h2>
                </div>
                <button data-testid="cart-close-button" onClick={() => setOpen(false)} aria-label="Chiudi carrello" className="flex h-10 w-10 items-center justify-center border border-neutral-800 text-neutral-400 hover:border-red-600 hover:text-red-500">
                  <X className="h-4 w-4" />
                </button>
              </header>

              <div className="flex-1 overflow-y-auto p-5">
                {items.length === 0 ? (
                  <p data-testid="cart-empty" className="border border-dashed border-neutral-800 p-8 text-center font-mono-tac text-xs uppercase tracking-[0.3em] text-neutral-500">Il carrello è vuoto</p>
                ) : (
                  <ul className="space-y-4">
                    {items.map((i) => (
                      <li key={`${i.product_id}-${i.size}`} data-testid="cart-item" className="flex gap-4 border-b border-neutral-800/60 pb-4">
                        <img src={resolveSrc(i.image)} alt="" className="h-20 w-20 shrink-0 border border-neutral-800 object-cover" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-neutral-100">{i.name}</p>
                          <p className="font-mono-tac mt-0.5 text-[10px] uppercase tracking-[0.2em] text-neutral-500">{i.size ? `Taglia ${i.size} · ` : ""}{euro(i.price_cents)}</p>
                          <div className="mt-3 flex items-center gap-2">
                            <button data-testid="cart-item-minus" onClick={() => setQty(i.product_id, i.size, i.quantity - 1)} aria-label="Diminuisci" className="flex h-7 w-7 items-center justify-center border border-neutral-700 text-neutral-300 hover:border-red-600"><Minus className="h-3 w-3" /></button>
                            <span data-testid="cart-item-qty" className="w-6 text-center font-mono-tac text-sm text-neutral-100">{i.quantity}</span>
                            <button data-testid="cart-item-plus" onClick={() => setQty(i.product_id, i.size, i.quantity + 1)} aria-label="Aumenta" className="flex h-7 w-7 items-center justify-center border border-neutral-700 text-neutral-300 hover:border-red-600"><Plus className="h-3 w-3" /></button>
                            <button data-testid="cart-item-remove" onClick={() => setQty(i.product_id, i.size, 0)} aria-label="Rimuovi" className="ml-auto text-neutral-600 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </div>
                        <p className="shrink-0 font-mono-tac text-sm text-neutral-100">{euro(i.price_cents * i.quantity)}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {items.length > 0 && (
                <footer className="border-t border-neutral-800 p-5">
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between text-neutral-400"><dt>Subtotale</dt><dd data-testid="cart-subtotal">{euro(subtotal)}</dd></div>
                    <div className="flex justify-between text-neutral-400"><dt>Spedizione Italia</dt><dd>{euro(shipping)}</dd></div>
                    <div className="flex justify-between pt-2 text-base font-bold text-neutral-100"><dt>Totale</dt><dd data-testid="cart-total">{euro(subtotal + shipping)}</dd></div>
                  </dl>
                  {promo ? (
                    <div data-testid="cart-promo-applied" className="mt-4 flex items-center justify-between gap-3 border border-dashed border-red-800/70 bg-red-950/20 px-3 py-2.5">
                      <p className="flex items-center gap-2 text-sm text-neutral-200"><Ticket className="h-4 w-4 shrink-0 text-red-500" /> Codice <span className="font-mono-tac tracking-[0.15em] text-neutral-50">{promo}</span> applicato al pagamento</p>
                      <button data-testid="cart-promo-remove" onClick={() => setPromo("")} aria-label="Rimuovi codice sconto" className="text-neutral-500 hover:text-red-500"><X className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <form
                      data-testid="cart-promo-form"
                      onSubmit={async (e) => {
                        e.preventDefault();
                        const code = promoInput.trim().toUpperCase();
                        if (!code) return;
                        const r = await fetch(`${API}/api/shop/promo/${encodeURIComponent(code)}`).catch(() => null);
                        if (!r?.ok) return toast("Codice non valido o scaduto", { description: code });
                        const d = await r.json();
                        setPromo(d.code);
                        setPromoInput("");
                        toast("Codice applicato", { description: `${d.code}: ${d.percent_off ? `-${d.percent_off}%` : `-${euro(d.amount_off_cents)}`} al pagamento` });
                      }}
                      className="mt-4 flex gap-2"
                    >
                      <input data-testid="cart-promo-input" value={promoInput} onChange={(e) => setPromoInput(e.target.value)} placeholder="Codice sconto (opzionale)" className="h-10 flex-1 border border-neutral-800 bg-black/40 px-3 font-mono-tac text-xs uppercase tracking-[0.15em] text-neutral-100 placeholder:normal-case placeholder:tracking-normal placeholder:text-neutral-600 focus:border-red-700 focus:outline-none" />
                      <button data-testid="cart-promo-apply" type="submit" className="h-10 border border-neutral-700 px-3 font-mono-tac text-[10px] uppercase tracking-[0.2em] text-neutral-200 hover:border-red-600 hover:text-red-500">Applica</button>
                    </form>
                  )}
                  <button data-testid="cart-checkout-button" onClick={checkout} disabled={loading} className="mt-5 inline-flex h-13 w-full items-center justify-center gap-3 bg-[#C8102E] py-4 font-mono-tac text-sm uppercase tracking-[0.2em] text-white transition-all duration-200 hover:bg-[#FF2A4B] hover:shadow-[0_0_30px_rgba(200,16,46,0.5)] disabled:opacity-60">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Paga con carta"}
                  </button>
                  <p className="mt-3 text-center font-mono-tac text-[10px] uppercase tracking-[0.2em] text-neutral-600">Checkout sicuro Stripe · Spedizione solo in Italia{promo ? "" : " · Puoi inserire il codice anche al pagamento"}</p>
                </footer>
              )}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
